import { FC, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { UserDTO } from '@/types/api-types';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { USER_ROLES } from '@/constants/global-constants';
import {
  adminRoutes,
  controllerRoutes,
  executorRoutes,
} from '@/constants/route-settings';
import { ExplorerItemType } from '@/constants/table-columns-config';

interface TwoFactorAuthProps {
  totpKey: string;
  totpSecret: string;
  onVerify: (otp: string) => Promise<UserDTO>;
  isFirstTime?: boolean;
}

interface TwoFactorFormData {
  otp: string;
}

export const TwoFactorAuth: FC<TwoFactorAuthProps> = ({
  totpKey,
  totpSecret,
  onVerify,
  isFirstTime = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [isFirstTimeEnabled, setIsFirstTimeEnabled] = useState(isFirstTime);

  const form = useForm<TwoFactorFormData>({
    defaultValues: {
      otp: '',
    },
  });

  const onSubmit = async (data: TwoFactorFormData) => {
    try {
      setIsLoading(true);
      const user = await onVerify(data.otp);
      navigateBasedOnRole(user.role);
    } catch (error) {
      form.setError('otp', {
        type: 'manual',
        message: 'Невірний код авторизації',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const navigateBasedOnRole = (userType: string) => {
    switch (userType) {
      case USER_ROLES.ADMIN.value:
        navigate(adminRoutes.users);
        break;
      case USER_ROLES.CONTROLLER.value:
        navigate(`${controllerRoutes.myDocuments}`, {
          state: {
            uuid: '',
            entityType: ExplorerItemType.AGREEMENT,
          },
        });
        break;
      case USER_ROLES.EXECUTOR.value:
        navigate(`${executorRoutes.myDocuments}`, {
          state: {
            uuid: '',
            entityType: ExplorerItemType.AGREEMENT,
          },
        });
        break;
    }
  };

  if (!isFirstTimeEnabled) {
    return (
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex w-full flex-1 flex-col items-center justify-between gap-4"
        >
          <FormField
            control={form.control}
            name="otp"
            rules={{
              required: "Обов'язкове поле",
              pattern: {
                value: /^\d{6}$/,
                message: 'Код повинен містити 6 цифр',
              },
            }}
            render={({ field }) => (
              <FormItem className="w-full">
                <p className="mt-[78px] text-base text-blue-500">
                  Введіть ваш код авторизації
                </p>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Код авторизації"
                    type="text"
                    maxLength={6}
                    autoComplete="one-time-code"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            className="hover:bg-primary/90 w-full rounded-md bg-primary py-2 text-white"
          >
            {form.formState.isSubmitting ? 'Перевірка...' : 'Увійти'}
          </Button>
        </form>
      </Form>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="mt-4 flex flex-col">
        <h3 className="text-sm text-blue-500">Етап 1:</h3>
        <p className="text-sm font-medium">
          Інсталюйте додаток{' '}
          <span className="font-bold">Google Authenticator або Authy</span>
        </p>
      </div>

      <div className="flex flex-col">
        <h3 className="text-sm text-blue-500">Етап 2:</h3>
        <div className="mb-3 flex justify-between">
          <div className="flex-1 border-r border-separator pr-3">
            <h4 className="text-base font-medium">Відскануйте QR-код</h4>
            <div className="flex gap-2">
              <div className="rounded-lg border bg-white p-1 shadow-[0px_0px_2px_1px_rgba(0,0,0,0.25)]">
                <QRCodeSVG value={totpKey} size={96} />
              </div>
              <ol className="list-inside text-sm">
                <li className="text-xs">
                  1. Відкрийте додаток для авторизації
                </li>
                <li className="text-xs">2. Відскануйте QR код</li>
              </ol>
            </div>
          </div>

          <div className="flex flex-1 flex-col items-center pl-3">
            <h4 className="mb-4 text-base font-medium">Введіть ключ</h4>
            <p className="mb-1 text-xs">
              Введіть цей код в додаток аутентифікації:
            </p>
            <p className="break-all rounded-md font-mono font-bold text-blue-500">
              {totpSecret}
            </p>
          </div>
        </div>

        <div className="flex justify-center">
          <Button
            type="button"
            disabled={isLoading}
            onClick={() => setIsFirstTimeEnabled(false)}
            className="hover:bg-primary/90 w-48 rounded-md bg-primary py-2 text-white"
          >
            {isLoading ? 'Отримуємо код...' : 'Отримати код'}
          </Button>
        </div>
      </div>
    </div>
  );
};
